import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/ui/image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Roles } from '@/types/auth/auth';

interface ProfileFieldsProps {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  avatar: string | undefined;
  createdAt: string | null;
  updatedAt: string | null;
  isInternalAdmin: boolean;
  onFieldChange: (field: 'full_name' | 'role' | 'email', value: string) => void;
  onAvatarChange: (url: string | undefined) => void;
  newPassword: string;
  onNewPasswordChange: (value: string) => void;
}

export const ProfileFields = ({
  userId,
  fullName,
  email,
  role,
  avatar,
  createdAt,
  updatedAt,
  isInternalAdmin,
  onFieldChange,
  onAvatarChange,
  newPassword,
  onNewPasswordChange,
}: ProfileFieldsProps) => {
  const roleOptions = [
    { value: Roles.Admin, label: "Admin" },
    { value: Roles.Business, label: "Business" },
    { value: Roles.Consumer, label: "Consumer" },
  ];

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="flex gap-6 flex-wrap">
      <div className="flex-1 min-w-[260px] space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="full-name">Full Name</Label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => copyToClipboard(fullName, "Full name")}
              title="Copy Full Name"
            >
              <Copy className="h-3 w-3 text-gray-400" />
            </Button>
          </div>
          <Input
            id="full-name"
            placeholder="Enter full name"
            value={fullName}
            onChange={(e) => onFieldChange("full_name", e.target.value)}
          />
          <p className="text-xs text-gray-500">The user's display name</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => copyToClipboard(email, "Email")}
              title="Copy Email"
            >
              <Copy className="h-3 w-3 text-gray-400" />
            </Button>
          </div>
          <Input
            id="email"
            type="email"
            value={email}
            disabled={!isInternalAdmin}
            className={!isInternalAdmin ? "bg-gray-50" : ""}
            onChange={(e) => onFieldChange("email", e.target.value)}
          />
          <p className="text-xs text-gray-500">
            {isInternalAdmin ? "Email updates will sync with authentication system" : "Only admins can update email addresses"}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select value={role} onValueChange={(value) => onFieldChange("role", value)} disabled={!isInternalAdmin}>
            <SelectTrigger id="role">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            {isInternalAdmin ? "User's permission level in the system" : "Only admins can update user roles"}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password">Reset Password</Label>
          <Input
            id="new-password"
            type="password"
            placeholder="Leave empty to keep current password"
            value={newPassword}
            onChange={(e) => onNewPasswordChange(e.target.value)}
          />
          <p className="text-xs text-gray-500">Minimum 6 characters</p>
        </div>
      </div>

      <div className="flex-1 min-w-[260px] space-y-6">
        <ImageUpload
          bucket="genie-images"
          value={avatar}
          onChange={onAvatarChange}
          label="Profile Avatar"
          className="max-w-[200px]"
        />

        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-700">User ID</p>
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-gray-500 break-all">{userId}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={() => copyToClipboard(userId, "User ID")}
              title="Copy User ID"
            >
              <Copy className="h-3.5 w-3.5 text-gray-400" />
            </Button>
          </div>
        </div>

        {createdAt && (
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Account Created</p>
            <p className="text-sm text-gray-500">{new Date(createdAt).toLocaleString()}</p>
          </div>
        )}

        {updatedAt && (
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Last Updated</p>
            <p className="text-sm text-gray-500">{new Date(updatedAt).toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
};
